import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import QuestionBankService from '../../_services/QuestionBankService';
import type {
  ConvertedQuestionSummary,
  ConvertWarning,
} from '../../_services/QuestionBankService';
import './import-docx.css';

declare global {
  interface Window {
    MathJax?: any;
  }
}

const TYPE_LABELS: Record<string, string> = {
  single_choice: 'SCQ',
  multiple_choice: 'MCQ',
  integer: 'Integer',
  true_false: 'True/False',
  match_the_following: 'MTF',
  fill_in_the_blanks: 'Blanks',
  text: 'Text',
  comprehension: 'Comprehension',
};

const TYPE_COLORS: Record<string, { bg: string; fg: string }> = {
  single_choice: { bg: '#e3f2fd', fg: '#1565c0' },
  multiple_choice: { bg: '#f3e5f5', fg: '#7b1fa2' },
  integer: { bg: '#fff3e0', fg: '#e65100' },
  true_false: { bg: '#fce4ec', fg: '#c62828' },
  match_the_following: { bg: '#e0f7fa', fg: '#00695c' },
  fill_in_the_blanks: { bg: '#ede7f6', fg: '#4527a0' },
  text: { bg: '#efebe9', fg: '#4e342e' },
  comprehension: { bg: '#e8eaf6', fg: '#283593' },
};

interface ConvertResult {
  aiEnabled: boolean;
  aiUsed: boolean;
  aiReason?: string;
  questions: ConvertedQuestionSummary[];
  warnings: ConvertWarning[];
  imageConversion?: { converted: number; failed: number; skipped: number };
  legacyEquationCount?: number;
  ambiguousBlockCount?: number;
  convertedDocxUrl?: string | null;
  convertedDocxBase64?: string;
  transactionId?: number | null;
  durationMs?: number;
}

const ConvertDocx: React.FC = () => {
  const [stage, setStage] = useState<'upload' | 'preview'>('upload');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [useLLM, setUseLLM] = useState(true);
  const [result, setResult] = useState<ConvertResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewListRef = useRef<HTMLDivElement>(null);

  // After the preview renders, ask MathJax to typeset any <math> elements
  // that were injected via dangerouslySetInnerHTML. Without this, browser
  // native MathML rendering often flattens <mtable> rows/columns to inline
  // text for content injected after page load.
  useEffect(() => {
    if (stage !== 'preview' || !previewListRef.current) return;
    if (!window.MathJax?.Hub?.Queue) return;
    try {
      window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, previewListRef.current]);
    } catch (e) {
      console.warn('MathJax typeset failed:', e);
    }
  }, [stage, result?.questions]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] || null);
    setError('');
  };

  const handleConvert = useCallback(async () => {
    if (!selectedFile) {
      setError('Please select a .docx file');
      return;
    }
    if (!selectedFile.name.toLowerCase().endsWith('.docx')) {
      setError('Only .docx files are supported');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await QuestionBankService.convertDocx(selectedFile, { useLLM });
      if (r.success && r.questions) {
        setResult({
          aiEnabled: r.aiEnabled ?? false,
          aiUsed: r.aiUsed ?? false,
          aiReason: r.aiReason,
          questions: r.questions,
          warnings: r.warnings ?? [],
          imageConversion: r.imageConversion,
          ambiguousBlockCount: r.ambiguousBlockCount,
          convertedDocxUrl: r.convertedDocxUrl,
          convertedDocxBase64: r.convertedDocxBase64,
          transactionId: r.transactionId,
          durationMs: r.durationMs,
        });
        setStage('preview');
      } else {
        setError(r.error || 'Failed to convert DOCX');
      }
    } catch (e: any) {
      setError(e.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  }, [selectedFile, useLLM]);

  const handleReset = () => {
    setStage('upload');
    setSelectedFile(null);
    setResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownload = () => {
    if (!result) return;
    if (result.convertedDocxUrl) {
      window.open(result.convertedDocxUrl, '_blank');
      return;
    }
    if (result.convertedDocxBase64) {
      const bin = atob(result.convertedDocxBase64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(selectedFile?.name || 'converted').replace(/\.docx$/i, '')}_converted.docx`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  /* --------------------------- Upload stage --------------------------- */

  const renderUpload = () => (
    <div className="import-docx-upload">
      <h5>Convert a Word document to the Importer format</h5>
      <p className="text-muted" style={{ fontSize: '0.9rem' }}>
        Use this when your source .docx isn&apos;t already in the strict table format the{' '}
        <Link to="/question-bank/import">Import Questions</Link> page expects. Upload any
        free-flowing question paper (numbered stems like <code>1.</code>, options as{' '}
        <code>(A)/(B*)/(C)/(D)</code>, <code>Ans.</code> markers for numerical answers, etc.) and we&apos;ll
        rewrite it into the marker-table format while preserving all embedded math and images.
      </p>

      <div className="import-docx-format-guide">
        <h6>How it works</h6>
        <ol style={{ fontSize: '0.85rem', color: 'var(--text-body)', paddingLeft: 20 }}>
          <li>
            <strong>Heuristic pass (always runs, free):</strong> regex detects section headers (e.g.{' '}
            <em>Single Choice Questions</em>, <em>Numerical Type Questions</em>), question numbers,
            option markers <code>(A) (B*) (C)</code>, and <code>Ans.</code> values.
          </li>
          <li>
            <strong>AI cleanup (optional, metered):</strong> if your paper has paragraphs the regex
            can&apos;t confidently label, we send those (and only those) to Claude in a single per-paper
            call to fill the gaps. One conversion = at most one API call. Counted against your AI
            quota only when the AI is actually invoked.
          </li>
          <li>
            <strong>WMF/EMF → JPG:</strong> legacy Word equation images are auto-converted to JPG at
            their original dimensions, so the converted file renders everywhere.
          </li>
          <li>
            <strong>Download &amp; import:</strong> review the preview below, download the converted
            .docx, then upload it to <Link to="/question-bank/import">Import Questions</Link>.
          </li>
        </ol>
      </div>

      <div className="import-docx-file-picker">
        <Form.Group>
          <Form.Label>Select DOCX File</Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept=".docx"
            onChange={handleFileChange}
          />
        </Form.Group>
        {selectedFile && (
          <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text-body)' }}>
            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
          </div>
        )}
      </div>

      <Form.Check
        type="switch"
        id="convert-use-llm"
        checked={useLLM}
        onChange={(e) => setUseLLM(e.target.checked)}
        label={
          <span>
            Use AI to clean up unclear sections
            <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginLeft: 8 }}>
              (counts against your AI quota only if needed)
            </span>
          </span>
        }
        style={{ marginTop: 16 }}
      />

      {error && (
        <div className="alert alert-danger" style={{ marginTop: 12 }}>
          {error}
        </div>
      )}

      <Button
        variant="primary"
        onClick={handleConvert}
        disabled={!selectedFile || loading}
        style={{ marginTop: 16 }}
      >
        {loading ? (
          <>
            <Spinner role="status" animation="border" size="sm" /> Converting…
          </>
        ) : (
          'Convert'
        )}
      </Button>
      {loading && (
        <span style={{ marginLeft: 12, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          This can take 10–30 seconds for documents with many embedded equations.
        </span>
      )}
    </div>
  );

  /* --------------------------- Preview stage --------------------------- */

  const renderPreview = () => {
    if (!result) return null;
    const errors = result.warnings.filter((w) => w.severity === 'error');
    const warns = result.warnings.filter((w) => w.severity === 'warn');
    const infos = result.warnings.filter((w) => w.severity === 'info');

    return (
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <h5 style={{ margin: 0 }}>
              Conversion complete — {result.questions.length} question
              {result.questions.length !== 1 ? 's' : ''} detected
            </h5>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 4 }}>
              {result.aiUsed ? (
                <span>
                  <span
                    style={{
                      background: '#e8f5e9',
                      color: '#2e7d32',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontWeight: 600,
                    }}
                  >
                    AI assisted
                  </span>{' '}
                  &middot; {result.ambiguousBlockCount} ambiguous paragraph
                  {result.ambiguousBlockCount === 1 ? '' : 's'} were classified by Claude.
                </span>
              ) : !result.aiEnabled ? (
                <span style={{ color: '#c62828' }}>
                  AI assistance unavailable {result.aiReason ? `(${result.aiReason})` : ''}. Used
                  rules only.
                </span>
              ) : (
                <span>
                  Resolved entirely by rules — no AI call needed{result.aiReason ? ` (${result.aiReason})` : ''}.
                </span>
              )}
              {typeof result.durationMs === 'number' && (
                <span> &middot; took {(result.durationMs / 1000).toFixed(1)}s</span>
              )}
              {result.imageConversion && (result.imageConversion.converted > 0 || result.imageConversion.failed > 0) && (
                <span>
                  {' '}
                  &middot; converted {result.imageConversion.converted} WMF/EMF image
                  {result.imageConversion.converted === 1 ? '' : 's'} to JPG
                  {result.imageConversion.failed > 0 ? `, ${result.imageConversion.failed} failed` : ''}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={handleReset}>
              Convert another
            </Button>
            <Button variant="primary" onClick={handleDownload}>
              Download converted .docx
            </Button>
          </div>
        </div>

        {result.legacyEquationCount && result.legacyEquationCount > 0 ? (
          <div
            className="alert alert-warning"
            style={{ marginBottom: 16, padding: '12px 16px' }}
          >
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Your document uses legacy MS Equation Editor 3.0 ({result.legacyEquationCount} equation
              {result.legacyEquationCount === 1 ? '' : 's'}).
            </div>
            <div style={{ fontSize: '0.88rem', marginBottom: 6 }}>
              We&apos;ve rasterised the equations to JPG so the converted file imports cleanly, but the
              math will appear as static images instead of crisp re-flowable formulas. For best
              quality, open the source file in Word and convert the equations to the modern format,
              then re-upload.
            </div>
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>
                Show me how to convert equations in Word
              </summary>
              <div style={{ marginTop: 10, fontSize: '0.88rem', lineHeight: 1.6 }}>
                <ol style={{ paddingLeft: 24 }}>
                  <li>
                    Open <code>{selectedFile?.name || 'your file'}</code> in Microsoft Word
                    (Windows or Mac &mdash; both work the same way).
                  </li>
                  <li>
                    Click on any equation in the document. The <strong>Equation</strong> tab appears
                    in the ribbon at the top.
                  </li>
                  <li>
                    On the <strong>Equation</strong> tab (far right of the ribbon), click{' '}
                    <strong>Convert</strong>{' '}
                    <span style={{ color: 'var(--text-muted)' }}>(icon shows an equation with a small arrow).</span>
                  </li>
                  <li>
                    In the dropdown that opens, choose{' '}
                    <strong>&ldquo;All &mdash; Office Math&rdquo;</strong>. Word will replace every
                    legacy equation in the file with native Office Math.
                  </li>
                  <li>
                    Save the file with <strong>File &rarr; Save As &rarr; Word Document (.docx)</strong>{' '}
                    (overwriting is fine).
                  </li>
                  <li>
                    Come back here and upload the saved file. The preview should now show crisp
                    in-line math instead of fuzzy images.
                  </li>
                </ol>
                <div
                  style={{
                    background: '#fff8e1',
                    border: '1px solid #ffe082',
                    borderRadius: 4,
                    padding: '8px 12px',
                    marginTop: 10,
                  }}
                >
                  <strong>Don&apos;t see the Convert button?</strong> Some Word versions surface a
                  yellow banner at the top of the document the first time you open it: &ldquo;Some
                  equations were created using an older version of Equation Editor. Convert them to
                  use new editing features?&rdquo; &mdash; click <strong>Convert</strong> on that
                  banner and skip to step 5. As a last resort, right-click any equation &rarr;{' '}
                  <strong>Equation Object</strong> &rarr; <strong>Convert</strong>.
                </div>
              </div>
            </details>
          </div>
        ) : null}

        {(errors.length > 0 || warns.length > 0 || infos.length > 0) && (
          <div style={{ marginBottom: 16 }}>
            {errors.map((w, i) => (
              <div key={`e${i}`} className="alert alert-danger" style={{ marginBottom: 6, padding: '6px 12px' }}>
                {w.message}
              </div>
            ))}
            {warns.map((w, i) => (
              <div key={`w${i}`} className="alert alert-warning" style={{ marginBottom: 6, padding: '6px 12px' }}>
                {w.message}
              </div>
            ))}
            {infos.map((w, i) => (
              <div key={`i${i}`} className="alert alert-info" style={{ marginBottom: 6, padding: '6px 12px' }}>
                {w.message}
              </div>
            ))}
          </div>
        )}

        <div className="alert alert-info" style={{ padding: '8px 12px', fontSize: '0.88rem' }}>
          <strong>Next step:</strong> Click <em>Download converted .docx</em>, then upload that file
          to the <Link to="/question-bank/import">Import Questions</Link> page. The import preview
          will let you assign tags and validate every question before saving.
        </div>

        <div className="convert-preview-list" ref={previewListRef}>
          {result.questions.map((q) => {
            const colors = TYPE_COLORS[q.type] ?? { bg: '#eee', fg: '#333' };
            const label = TYPE_LABELS[q.type] ?? q.type;
            // MTF is included here so the (A)/(B)/(C)/(D) match-combination
            // options under each MTF question are shown in the preview the
            // same way SCQ/MCQ choices are.
            const choiceType = ['single_choice', 'multiple_choice', 'true_false', 'match_the_following'].includes(q.type);
            const ok = choiceType ? q.optionCount >= 2 && q.hasCorrect : q.hasAnswer;

            // Comprehension parents have no Q-number of their own — the
            // children carry the actual question numbers (Q9, Q10, …). The
            // parent header just shows the slot + type pill + status.
            const isComp = q.type === 'comprehension';
            const headerLabel = isComp ? '' : `Q${q.number ?? q.index}`;

            return (
              <div
                key={q.index}
                style={{
                  border: '1px solid var(--border-default)',
                  borderRadius: 6,
                  padding: 12,
                  marginBottom: 12,
                  background: 'var(--surface-card)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <strong>
                    {headerLabel}
                    <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>
                      {headerLabel ? ' ' : ''}(slot {q.index})
                    </span>
                  </strong>
                  <span
                    style={{
                      background: colors.bg,
                      color: colors.fg,
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </span>
                  {ok ? (
                    <span style={{ color: '#2e7d32', fontWeight: 600, fontSize: '0.85rem' }}>OK</span>
                  ) : (
                    <span style={{ color: '#c62828', fontWeight: 600, fontSize: '0.85rem' }}>
                      Needs review
                    </span>
                  )}
                </div>

                <div className="convert-preview-stem" style={{ marginBottom: 8 }}>
                  {q.stemHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: q.stemHtml }} />
                  ) : q.stemPreview ? (
                    <div>{q.stemPreview}</div>
                  ) : (
                    <span style={{ color: 'var(--text-faint)' }}>(image / math only)</span>
                  )}
                </div>

                {choiceType && q.choicesPreview && q.choicesPreview.length > 0 && (
                  <ol type="A" style={{ marginBottom: 0, paddingLeft: 24 }}>
                    {q.choicesPreview.map((c, ci) => (
                      <li
                        key={ci}
                        style={{
                          color: c.isCorrect ? '#2e7d32' : 'inherit',
                          fontWeight: c.isCorrect ? 600 : 400,
                          marginBottom: 2,
                        }}
                      >
                        <span dangerouslySetInnerHTML={{ __html: c.textHtml || '<em>(empty)</em>' }} />
                        {c.isCorrect && (
                          <span style={{ marginLeft: 6, fontSize: '0.85rem' }}>✓ correct</span>
                        )}
                      </li>
                    ))}
                  </ol>
                )}

                {!choiceType && q.answerHtml && (
                  <div style={{ marginTop: 4 }}>
                    <strong style={{ fontSize: '0.85rem', color: 'var(--text-body)' }}>Answer: </strong>
                    <span dangerouslySetInnerHTML={{ __html: q.answerHtml }} />
                  </div>
                )}

                {q.type === 'comprehension' && q.childrenPreview && q.childrenPreview.length > 0 && (
                  <div style={{ marginTop: 12, borderTop: '1px dashed var(--border-default)', paddingTop: 12 }}>
                    {q.childrenPreview.map((child, ci) => {
                      const childChoiceType = ['single_choice', 'multiple_choice', 'true_false', 'match_the_following'].includes(child.type);
                      return (
                        <div key={ci} style={{ marginBottom: ci < q.childrenPreview!.length - 1 ? 16 : 0 }}>
                          <div style={{ fontSize: '0.95rem', marginBottom: 4 }}>
                            <strong>
                              {child.number !== undefined ? `Q${child.number}` : `Sub-question ${ci + 1}`}
                            </strong>
                            <span style={{ marginLeft: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                              {TYPE_LABELS[child.type] ?? child.type}
                            </span>
                          </div>
                          {child.stemHtml && (
                            <div style={{ marginBottom: 4 }} dangerouslySetInnerHTML={{ __html: child.stemHtml }} />
                          )}
                          {childChoiceType && child.choicesPreview && child.choicesPreview.length > 0 && (
                            <ol type="A" style={{ marginBottom: 0, paddingLeft: 24 }}>
                              {child.choicesPreview.map((c, cci) => (
                                <li
                                  key={cci}
                                  style={{
                                    color: c.isCorrect ? '#2e7d32' : 'inherit',
                                    fontWeight: c.isCorrect ? 600 : 400,
                                    marginBottom: 2,
                                  }}
                                >
                                  <span dangerouslySetInnerHTML={{ __html: c.textHtml || '<em>(empty)</em>' }} />
                                  {c.isCorrect && (
                                    <span style={{ marginLeft: 6, fontSize: '0.85rem' }}>✓ correct</span>
                                  )}
                                </li>
                              ))}
                            </ol>
                          )}
                          {!childChoiceType && child.answerHtml && (
                            <div style={{ marginTop: 4 }}>
                              <strong style={{ fontSize: '0.85rem', color: 'var(--text-body)' }}>Answer: </strong>
                              <span dangerouslySetInnerHTML={{ __html: child.answerHtml }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="import-docx-container" style={{ padding: 24 }}>
      {stage === 'upload' ? renderUpload() : renderPreview()}
    </div>
  );
};

export default ConvertDocx;
